import { Modal } from '@pmi/dsm-react';
import { ReactNode, useState } from 'react';
import { useSelector } from 'react-redux';
import { toastWithAnalytics } from '../../../../../../shared/analytics';
import { getUserText } from '../../../../../../shared/helpers';
import { User } from '../../../../../../shared/models/UserModels';
import { accountSelector } from '../../../../../../shared/store/shared/accountSlice';
import { useDeleteUserMutation } from './apiSlice';
import './styles.module.scss';

interface DeleteUserModalProps {
  user: User;
  onCancel?: () => void;
  onSuccess?: () => void;
}

export default function DeleteUserModal({
  user,
  onSuccess = () => {},
  onCancel = () => {},
}: DeleteUserModalProps) {
  const { currentAccountId } = useSelector(accountSelector);
  const [isProcessing, setIsProcessing] = useState(false);
  const onCancelHandler = () => {
    onCancel();
  };

  const [deleteUser, deletedUser] = useDeleteUserMutation();

  const action = deleteUser;
  const actioned = deletedUser;

  const onDelete = () => {
    setIsProcessing(true);
    action({ accountId: currentAccountId, email: user.email })
      .unwrap()
      .then(() => {
        const message = (): ReactNode => (
          <>
            You have deleted <span data-exclude-lg>{getUserText(user)}</span>
          </>
        );
        toastWithAnalytics(message(), 'success');
        actioned.reset();
        onSuccess();
        onCancel();
      })
      .catch(() => {
        const errorMessage = (): ReactNode => (
          <>
            Could not delete <span data-exclude-lg>{getUserText(user)}</span>{' '}
            Please try again.
          </>
        );
        toastWithAnalytics(errorMessage(), 'danger');
        setIsProcessing(false);
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  if (!user) return null;

  return (
    <Modal
      show
      customClassName="forbidRestoreUser"
      onClose={onCancelHandler}
      headline={`Are you sure you want to delete ${getUserText(user)}?`}
      variant="input"
      buttons={[
        {
          titleText: 'Cancel',
          variant: 'outline-secondary',
          onClick: onCancelHandler,
          disabled: isProcessing,
        },
        {
          titleText: 'Delete User',
          variant: 'primary',
          onClick: onDelete,
          disabled: isProcessing,
        },
      ]}
    >
      <div className="d-flex flex-column">
        <p className="mb-4">
          Deleting this user will permanently remove them from the account.
        </p>
      </div>
    </Modal>
  );
}
